import React from "react";
import "../../index.css";
import MonthAndYearDd from "../MonthAndYearDd";
import ChevronButton from "../ChevronBtn";
import useDateLogic from "../../hooks/useDateLogic";

export interface DatePickerProps {
  preSelectedDate?: Date | null;
  onChange: (date: Date) => void;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  customDaysOfWeek?: string[];
  containerStyle?: string;
  daysOfWeekStyles?: string;
  visibleDatesStyle?: string;
  isSelectedStyle?: string;
  disabledDate?: Date;
  showYearMonthDivider?: boolean;
  prevMonthBtnIcon?: React.ReactNode;
  nextMonthBtnIcon?: React.ReactNode;
  outlineStyle?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  preSelectedDate,
  onChange,
  weekStartsOn = 0,
  customDaysOfWeek,
  containerStyle,
  daysOfWeekStyles,
  visibleDatesStyle,
  isSelectedStyle,
  disabledDate,
  showYearMonthDivider,
  prevMonthBtnIcon,
  nextMonthBtnIcon,
  outlineStyle,
}) => {
  const datePickerLogic = useDateLogic({
    preSelectedDate,
    weekStartsOn,
    customDaysOfWeek,
    onChange,
  });
  const outline = outlineStyle ?? "outline-blue-500"

  return (
    <div data-testid="container" className={containerStyle ?? `py-4 px-2 border rounded shadow-md`}>
      <div className="flex justify-between items-center mb-4">
        {!prevMonthBtnIcon ? (
          <ChevronButton
            onClick={datePickerLogic.handlePreviousMonth}
            direction="left"
            extraStyles={outline}
          />
        ) : (
          <button data-testid="prevMonthBtnIcon" onClick={datePickerLogic.handlePreviousMonth}>
            {prevMonthBtnIcon}
          </button>
        )}
        <MonthAndYearDd
          monthsRange={datePickerLogic.monthsRange}
          handleMonthChange={datePickerLogic.handleMonthChange}
          currentDate={datePickerLogic.currentDate}
          setCurrentDate={datePickerLogic.setCurrentDate}
          showYearMonthDivider={showYearMonthDivider}
        />
        {!nextMonthBtnIcon ? (
          <ChevronButton
            onClick={datePickerLogic.handleNextMonth}
            direction="right"
            extraStyles={outline}
          />
        ) : (
          <button onClick={datePickerLogic.handleNextMonth}>
            {nextMonthBtnIcon}
          </button>
        )}
      </div>

      {/* Days of the Week */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {datePickerLogic.daysOfWeek.map((day) => (
          <div
            key={day}
            data-testid="day-of-week"
            className={
              daysOfWeekStyles ?? `text-center font-medium text-black/70`
            }
          >
            {day}
          </div>
        ))}
      </div>

      {/* Dates of the Month */}
      <div className="grid grid-cols-7 gap-1 text-black text-opacity-55">
        {datePickerLogic.dates.map((date) => {
          const isCurrentMonth =
            date.getMonth() === datePickerLogic.currentDate.getMonth();
          const isSelected =
            datePickerLogic.selectedDate &&
            date.toDateString() === datePickerLogic.selectedDate.toDateString();

          const isDisabled =
            date.toDateString() === disabledDate?.toDateString();

          return (
            <button
              key={date.toISOString()}
              className={`p-2 text-center rounded ${
                isCurrentMonth
                  ? isSelected && !isDisabled
                    ? `${isSelectedStyle ?? 'bg-blue-500 text-white'}`
                    : isDisabled
                    ? "cursor-not-allowed hover:bg-transparent text-black text-opacity-25"
                    : `${
                        visibleDatesStyle ?? `hover:bg-blue-100 cursor-pointer`
                      }`
                  : "invisible"
              }`}
              disabled={isDisabled}
              onClick={
                isCurrentMonth
                  ? () => datePickerLogic.handleDateChange(date)
                  : undefined
              }
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DatePicker;
